import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import StoreFormActions from "@store/Components/Store/StoreFormActions";
import Checkbox from "@store/MobiusOverrides/Checkbox/Checkbox";
import submitStatementOptions from "@store/Store/Data/StatementOptions/Handlers/SubmitStatementOptionsForm";
import { formGridProps } from "@store/theme/common/props/form";
import parse from "html-react-parser";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    modalContent = "modalContent",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    successMessage = "successMessage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.modalContent]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.successMessage]: string;
    };
}

const mapDispatchToProps = {
    submitStatementOptions,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface StatementOptionsFormStyles {
    container?: GridContainerProps;
    formGridItem?: GridItemProps;
    select?: SelectPresentationProps;
    emailAddress?: TextFieldPresentationProps;
    submitButton?: ButtonPresentationProps;
    termsAndConditionsModal?: ModalPresentationProps;
    printableVersionLink?: LinkPresentationProps;
}

export const statementOptionsFormStyles: StatementOptionsFormStyles = {
    container: formGridProps,
    formGridItem: {
        width: [12, 12, 8, 8, 8],
    },
    printableVersionLink: {
        css: css`
            ${getProp("theme.link.defaultProps.css")}
            margin-bottom: 1em;
        `,
    },
};

const styles = statementOptionsFormStyles;
const StyledForm = getStyledWrapper("form");
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
const isNonEmptyString = (value: string | undefined) => value !== undefined && value.trim() !== "";

const StatementOptionsForm: React.FC<Props> = ({
    fields: { modalContent, buttonLink: buttonLinkField, buttonLabel, successMessage },
    submitStatementOptions,
}) => {
    const buttonLink = useGetLink(buttonLinkField);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toasterContext = React.useContext(ToasterContext);
    const [isTermsAndConditionsModalOpen, setIsTermsAndConditionsModalOpen] = useState(false);
    const onTermsAndConditionsClose = () => {
        setIsTermsAndConditionsModalOpen(false);
    };

    const [statementOption, setStatementOption] = useState("");
    const [emailAddress, setEmailAddress] = useState("");
    const [confirmEmailAddress, setConfirmEmailAddress] = useState("");
    const [termsAndConditionsAgreement, setTermsAndConditionsAgreement] = useState(false);

    const [statementOptionError, setStatementOptionError] = useState("");
    const [emailAddressError, setEmailAddressError] = useState("");
    const [confirmEmailAddressError, setConfirmEmailAddressError] = useState("");
    const [termsAndConditionsAgreementError, setTermsAndConditionsAgreementError] = useState("");

    const [showFormErrors, setShowFormErrors] = useState(false);

    const handleStatementOptionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setStatementOption(event.currentTarget.value);
        validateStatementOption(event.currentTarget.value);
    };

    const handleEmailAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailAddress(event.currentTarget.value);
        validateEmailAddress(event.currentTarget.value);
    };

    const handleConfirmEmailAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmEmailAddress(event.currentTarget.value);
        validateConfirmEmailAddress(event.currentTarget.value);
    };

    const handleTermsAndConditionsAgreementChange = (_: React.SyntheticEvent<Element, Event>, value: boolean) => {
        setTermsAndConditionsAgreement(value);
        validateTermsAndConditionsAgreement(value);
    };

    const validateStatementOption = (statementOption: string) => {
        const statementOptionValid = isNonEmptyString(statementOption);
        setStatementOptionError(!statementOptionValid ? translate("Statement Option is required.") : "");
        return statementOptionValid;
    };

    const validateEmailAddress = (emailAddress: string) => {
        const emailAddressValid = emailRegexp.test(emailAddress);
        const emailAddressEmpty = isNonEmptyString(emailAddress);
        setEmailAddressError(
            !emailAddressEmpty
                ? translate("Email Address is required.")
                : !emailAddressValid
                ? translate("Email Address is invalid.")
                : "",
        );
        return emailAddressValid && emailAddressEmpty;
    };

    const validateConfirmEmailAddress = (confirmEmailAddress: string) => {
        const confirmEmailAddressValid = confirmEmailAddress === emailAddress;
        setConfirmEmailAddressError(!confirmEmailAddressValid ? translate("Email addresses do not match.") : "");
        return confirmEmailAddressValid;
    };

    const validateTermsAndConditionsAgreement = (termsAndConditionsAgreement: boolean) => {
        const termsAndConditionsAgreementValid = termsAndConditionsAgreement;
        setTermsAndConditionsAgreementError(
            !termsAndConditionsAgreementValid ? translate("Terms and conditions agreement is required.") : "",
        );
        return termsAndConditionsAgreementValid;
    };

    const validateForm = () => {
        const statementOptionValid = validateStatementOption(statementOption);
        const emailAddressValid = validateEmailAddress(emailAddress);
        const confirmEmailAddressValid = validateConfirmEmailAddress(confirmEmailAddress);
        const termsAndConditionsAgreementValid = validateTermsAndConditionsAgreement(termsAndConditionsAgreement);

        return (
            statementOptionValid && emailAddressValid && confirmEmailAddressValid && termsAndConditionsAgreementValid
        );
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!validateForm()) {
            setShowFormErrors(true);
            return false;
        }

        submitStatementOptions({
            apiParameter: {
                HasOptedIn: statementOption === "Opt in",
                Email: emailAddress,
                ConfirmationEmail: confirmEmailAddress,
            },
            onSuccess: () => {
                toasterContext.addToast({
                    body: <Typography>{successMessage}</Typography>,
                    messageType: "success",
                });
                setStatementOption("");
                setEmailAddress("");
                setConfirmEmailAddress("");
                setTermsAndConditionsAgreement(false);
            },
        });

        setIsSubmitting(true);
    };

    return (
        <>
            <StyledForm
                id="statementOptionsForm"
                onSubmit={handleFormSubmit}
                noValidate={true}
                data-test-selector="statementOptionsForm"
            >
                <GridContainer {...styles.container}>
                    <GridItem {...styles.formGridItem}>
                        <Select
                            {...styles.select}
                            onChange={handleStatementOptionChange}
                            error={statementOptionError}
                            value={statementOption}
                            label={translate("Statement Option")}
                            required
                        >
                            <option value="">{translate("Select Statement Option")}</option>
                            <option key="Opt in" value="Opt in">
                                {translate("Opt in")}
                            </option>
                            <option key="Opt out" value="Opt out">
                                {translate("Opt out")}
                            </option>
                        </Select>
                    </GridItem>
                    <GridItem {...styles.formGridItem}>
                        <TextField
                            {...styles.emailAddress}
                            onChange={handleEmailAddressChange}
                            value={emailAddress}
                            error={emailAddressError}
                            type="email"
                            label={translate("Email Address")}
                            required
                        />
                    </GridItem>
                    <GridItem {...styles.formGridItem}>
                        <TextField
                            {...styles.emailAddress}
                            onChange={handleConfirmEmailAddressChange}
                            value={confirmEmailAddress}
                            error={confirmEmailAddressError}
                            type="email"
                            label={translate("Confirm Email Address")}
                            required
                        />
                    </GridItem>
                    <GridItem {...styles.formGridItem}>
                        <CheckboxGroup error={termsAndConditionsAgreementError}>
                            <Checkbox
                                checked={termsAndConditionsAgreement}
                                onChange={handleTermsAndConditionsAgreementChange}
                                required
                            >
                                {`${translate("I agree to the")} `}
                                <Link type="button" onClick={() => setIsTermsAndConditionsModalOpen(true)}>
                                    {translate("StatementOptions_TermsAndConditions")}
                                </Link>
                            </Checkbox>
                        </CheckboxGroup>
                    </GridItem>
                </GridContainer>
                <StoreFormActions>
                    <Button {...styles.submitButton} type="submit">
                        {translate("Submit")}
                    </Button>
                </StoreFormActions>
            </StyledForm>
            <Modal
                headline={translate("Terms and Conditions")}
                {...styles.termsAndConditionsModal}
                isOpen={isTermsAndConditionsModalOpen}
                handleClose={onTermsAndConditionsClose}
            >
                {buttonLink?.url ? (
                    <Link {...styles.printableVersionLink} href={buttonLink?.url} target="_blank">
                        {buttonLabel || buttonLink.title || buttonLink.url}
                    </Link>
                ) : null}
                <Typography>{parse(modalContent, parserOptions)}</Typography>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(StatementOptionsForm),
    definition: {
        group: "Basic",
        icon: "FormField",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.modalContent,
                editorTemplate: "RichTextField",
                displayName: "Modal Content",
                defaultValue: "",
            },
            {
                name: fields.buttonLink,
                displayName: "Printable Version Link",
                editorTemplate: "LinkField",
                fieldType: "Translatable",
                defaultValue: { type: "Url", value: "" },
            },
            {
                name: fields.buttonLabel,
                displayName: "Printable Version Link Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Printable Version",
                isVisible: widget => widget.fields.buttonLink?.value,
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
